export type AdminSettingsSection = {
  id: string;
  navLabel: string;
  title: string;
  path: string;
  description: string;
  tone: 'primary' | 'success' | 'warning' | 'info';
  readinessLabel: string;
  workspaceTitle: string;
  workspaceDescription: string;
  metrics: Array<{
    label: string;
    value: string;
    tone: 'primary' | 'success' | 'warning' | 'info';
  }>;
  highlights: string[];
  checklist: string[];
};

export const adminSettingsSections: AdminSettingsSection[] = [
  {
    id: 'overview',
    navLabel: 'Overview',
    title: 'Overview',
    path: '/admin-settings/overview',
    description: 'Tenant setup workspace for company structure, delegated admin ownership, current completion state, and readiness review.',
    tone: 'primary',
    readinessLabel: 'Use the selected company and next recommended action to decide where setup work moves next.',
    workspaceTitle: 'Section map',
    workspaceDescription: 'Use this landing page to orient Company Admins around the setup sequence, required objects, and the handoff to Scheduler Admins.',
    metrics: [
      { label: 'Overview route', value: 'Live', tone: 'success' },
      { label: 'Checklist stages', value: '6', tone: 'primary' },
      { label: 'Readiness review', value: 'Linked', tone: 'info' },
    ],
    highlights: [
      'Separate company setup from platform administration and downstream schedule operations.',
      'Keep the setup flow resumable so Company Admins can save progress and return later.',
      'Surface blockers before Scheduler Admin handoff begins.',
    ],
    checklist: [
      'Review tenant-backed counts and readiness totals for the selected company.',
      'Use the current company status and readiness badges to decide where to continue work.',
      'Follow any overview blockers directly into the affected setup sections.',
    ],
  },
  {
    id: 'company-profile',
    navLabel: 'Company Profile',
    title: 'Company Profile',
    path: '/admin-settings/company-profile',
    description: 'Manage company identity, primary contacts, branding defaults, tenant status, and onboarding metadata.',
    tone: 'info',
    readinessLabel: 'Use the saved profile record here as the source of truth for company identity and onboarding status.',
    workspaceTitle: 'Identity and ownership',
    workspaceDescription: 'Use this section to maintain the tenant-facing identity fields that must be confirmed before structural setup is considered complete.',
    metrics: [
      { label: 'Profile editor', value: 'Live', tone: 'success' },
      { label: 'Status control', value: 'Saved to API', tone: 'primary' },
      { label: 'Branding inputs', value: 'Available', tone: 'info' },
    ],
    highlights: [
      'Keep company metadata close to the admin who owns onboarding readiness.',
      'Show onboarding, active, or inactive tenant status clearly.',
      'Reserve platform-only controls for the separate CARES Administration surface.',
    ],
    checklist: [
      'Confirm company identity, contact, and status details for the selected tenant.',
      'Save profile details before moving into locations, service lines, and Scheduler Admin coverage.',
      'Use setup ownership and status fields to keep onboarding progress visible.',
    ],
  },
  {
    id: 'locations',
    navLabel: 'Locations',
    title: 'Locations',
    path: '/admin-settings/locations',
    description: 'Maintain the physical or operational sites that service lines, teams, and schedules will reference.',
    tone: 'success',
    readinessLabel: 'Use the table and import tools here to manage live location records for the selected company.',
    workspaceTitle: 'Location registry',
    workspaceDescription: 'Use this section to manage locations with import, table editing, ordering, status changes, and dependency warnings for related records.',
    metrics: [
      { label: 'Location table', value: 'Live', tone: 'success' },
      { label: 'Bulk entry', value: 'CSV/XLSX/Paste', tone: 'primary' },
      { label: 'Dependency checks', value: 'Inline', tone: 'info' },
    ],
    highlights: [
      'Treat locations as reusable setup objects, not one-time onboarding entries.',
      'Support spreadsheet paste and upload for faster onboarding.',
      'Expose where each location is used before allowing risky edits.',
    ],
    checklist: [
      'Use search, sort, and bulk actions to keep the location table current.',
      'Import XLSX, CSV, pasted rows, or simple lists through the preview workflow before commit.',
      'Review downstream service-line and schedule dependencies before deleting or deactivating a location.',
    ],
  },
  {
    id: 'groups',
    navLabel: 'Departments & Groups',
    title: 'Departments & Groups',
    path: '/admin-settings/groups',
    description: 'Maintain organizational Departments and the child Schedule Groups that own schedules, rules, requests, and publication.',
    tone: 'warning',
    readinessLabel: 'Use the shared workspace to maintain the selected company’s Department and Schedule Group hierarchy.',
    workspaceTitle: 'Department registry',
    workspaceDescription: 'This section uses the same shared company Department records and interface as CARES Administration.',
    metrics: [
      { label: 'Department table', value: 'Live', tone: 'success' },
      { label: 'Shared record', value: 'Company-wide', tone: 'info' },
      { label: 'Tenant scope', value: 'Enforced', tone: 'primary' },
    ],
    highlights: [
      'Use the same Departments and Schedule Groups maintained in CARES Administration for the selected client.',
      'Preserve parent-child relationships, status, and scheduling membership counts.',
      'Keep department changes tenant-scoped and immediately visible in both admin workspaces.',
    ],
    checklist: [
      'Keep department names, slugs, kinds, and parent relationships current.',
      'Review membership and child-group counts before deletion.',
      'Confirm department structure before downstream scheduling work relies on it.',
    ],
  },
  {
    id: 'scheduler-admins',
    navLabel: 'Scheduler Admins',
    title: 'Scheduler Admins',
    path: '/admin-settings/scheduler-admins',
    description: 'Review assigned Scheduler Admins and grant Department-wide or Schedule Group access.',
    tone: 'success',
    readinessLabel: 'Use saved assignments here to review who owns each service line and what scope still needs coverage.',
    workspaceTitle: 'Delegated ownership',
    workspaceDescription: 'Use this section to keep delegated schedule ownership explicit, auditable, and easy to review before handoff.',
    metrics: [
      { label: 'Assignment table', value: 'Live', tone: 'success' },
      { label: 'Scope dimensions', value: 'Type, line, optional narrowing', tone: 'primary' },
      { label: 'Coverage states', value: 'Tracked', tone: 'info' },
    ],
    highlights: [
      'A service line is not ready without at least one active Scheduler Admin.',
      'Assignments stay auditable and previewable before they take effect.',
      'Scheduler Admin access stays scoped rather than defaulting to full-company control.',
    ],
    checklist: [
      'Review pending, active, and deactivated coverage before handoff moves forward.',
      'Use the table to stage scoped Scheduler Admin assignments for review.',
      'Watch coverage warnings before removing or reassigning ownership.',
    ],
  },
  {
    id: 'help-setup-guide',
    navLabel: 'Help & Setup Guide',
    title: 'Company Admin Help & Setup Guide',
    path: '/admin-settings/help-setup-guide',
    description: 'Role-specific help, setup guidance, import guidance, and onboarding reference material for Company Admins.',
    tone: 'info',
    readinessLabel: 'Use this reference route for in-product guidance while working through live setup sections.',
    workspaceTitle: 'Durable reference',
    workspaceDescription: 'Use this page as the inline documentation hub behind each setup section so guidance stays close to the workflow.',
    metrics: [
      { label: 'Guide topics', value: '6', tone: 'success' },
      { label: 'Role cards', value: '3', tone: 'info' },
      { label: 'Quick links', value: 'Section routes', tone: 'primary' },
    ],
    highlights: [
      'Explain Company Admin responsibilities and where Scheduler Admin ownership begins.',
      'Centralize setup guidance, import formats, and table-management guidance.',
      'Give each setup screen a durable reference topic without cluttering the forms.',
    ],
    checklist: [
      'Use the guide topics to answer common setup, ownership, and readiness questions.',
      'Jump directly from each topic into the related setup section when action is needed.',
      'Keep reference examples aligned with the current company hierarchy model.',
    ],
  },
];
